import React from "react";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";

// The tallest bar's height; the column above it keeps room for the count.
const BAR_MAX_PX = 128;
const BAR_MIN_PX = 2;

/**
 * The bar where the funnel loses the most learners: the step after the largest fall from
 * one bar to the next (spec screen 5, "the step where the bars drop is where the content
 * loses people"). The first bar of a tie wins; no bar when nothing ever falls.
 *
 * @param {{ key: string, count: number }[]} funnel  enrolled first, then steps in order
 * @returns {string|null} the key of that bar
 */
export function largestDropKey(funnel) {
  let best = null;
  let drop = 0;
  for (let i = 1; i < funnel.length; i += 1) {
    const fall = funnel[i - 1].count - funnel[i].count;
    if (fall > drop) {
      drop = fall;
      best = funnel[i].key;
    }
  }
  return best;
}

/**
 * The step funnel (contract §5.1 `PathwayDashboard.funnel`): one bar per step, in order,
 * after the "Enrolled" bar, its height relative to the tallest bar, its count on top and
 * its title below. The largest drop is highlighted in the accent colour.
 *
 * It is a list, not a picture: each bar is an item whose text carries its title and count,
 * so a screen reader reads the funnel in order.
 *
 * @param {object} props
 * @param {{ key: string, title: string, count: number }[]} props.funnel
 */
/** The screen-reader sentence of one bar: singular or plural, largest drop or not. */
function funnelSrKey(drop, count) {
  if (drop) return count === 1 ? "path.dash.funnelDropSrOne" : "path.dash.funnelDropSr";
  return count === 1 ? "path.dash.funnelSrOne" : "path.dash.funnelSr";
}

export default function FunnelChart({ funnel }) {
  const t = useT("admin");
  const max = Math.max(1, ...funnel.map((b) => b.count));
  const dropKey = largestDropKey(funnel);
  return (
    <ol data-testid={IDS.funnel} className="flex items-end gap-3 overflow-x-auto pb-1">
      {funnel.map((bar) => {
        const drop = bar.key === dropKey;
        const label = bar.key === "enrolled" ? t("path.dash.enrolled") : bar.title;
        return (
          <li
            key={bar.key}
            data-testid={IDS.funnelBar(bar.key)}
            data-count={bar.count}
            data-drop={drop ? "true" : "false"}
            className="flex flex-col items-center justify-end gap-1 min-w-[5.5rem] flex-1"
          >
            <span aria-hidden="true" className="w-full h-40 flex flex-col items-center justify-end gap-1">
              <span className={`text-xs tabular-nums ${drop ? "font-semibold text-eh-ochre-deep" : "text-eh-muted"}`}>{bar.count}</span>
              <span
                className={`w-full rounded-t-lg ${drop ? "bg-eh-ochre-deep" : "bg-eh-sage-deep"}`}
                style={{ height: `${Math.max(BAR_MIN_PX, Math.round((bar.count / max) * BAR_MAX_PX))}px` }}
              />
            </span>
            <span className={`text-xs text-center leading-tight line-clamp-2 ${drop ? "font-semibold text-eh-ink" : "text-eh-muted"}`}>
              {label}
              <span className="sr-only">{` ${t(funnelSrKey(drop, bar.count), { n: bar.count })}`}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
